import React from 'react';
import { useTheme } from '../../context/ThemeContext';

export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  loading = false,
  disabled = false,
  className = '',
  onClick,
  ...props 
}) => {
  const { isDark } = useTheme();

  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: `bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 ${
      isDark ? 'shadow-lg shadow-primary-900/30' : 'shadow-md shadow-primary-500/20'
    }`,
    secondary: `bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 focus:ring-gray-500`,
    outline: `border-2 ${
      isDark 
        ? 'border-gray-600 text-gray-200 hover:bg-gray-700/50' 
        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
    } focus:ring-gray-500`,
    ghost: `${
      isDark 
        ? 'text-gray-300 hover:bg-gray-700/50' 
        : 'text-gray-600 hover:bg-gray-100'
    } focus:ring-gray-500`,
    danger: `bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 ${
      isDark ? 'shadow-lg shadow-red-900/30' : 'shadow-md shadow-red-500/20'
    }`,
    success: `bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500 ${
      isDark ? 'shadow-lg shadow-emerald-900/30' : 'shadow-md shadow-emerald-500/20'
    }`,
  };

  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs gap-1.5',
    sm: 'px-3 py-2 text-sm gap-2',
    md: 'px-4 py-2.5 text-sm gap-2',
    lg: 'px-6 py-3 text-base gap-2.5',
    xl: 'px-8 py-4 text-lg gap-3',
  };

  const iconSizes = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
  };

  return (
    <button
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${loading ? 'cursor-wait opacity-70' : ''}
        ${className}
      `}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && (
        <svg className="animate-spin w-4 h-4 mr-2" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {!loading && Icon && iconPosition === 'left' && <Icon className={iconSizes[size]} />}
      <span>{children}</span>
      {!loading && Icon && iconPosition === 'right' && <Icon className={iconSizes[size]} />}
    </button>
  );
};

